/**
 * DashboardLayout — protected shell for the dashboard (IA §1.1, §9).
 *
 * Layout rows (top-to-bottom):
 *   - SkipToContent: visually-hidden keyboard-first link to the main region.
 *   - Top nav: product name + 6 primary tab links + stale-status placeholder.
 *     On mobile the tab list switches to a horizontally scrollable strip with
 *     scroll-snap so each tab snaps into place — the full label set stays
 *     accessible without truncating or stacking.
 *   - FilterBar: global filter strip (IA §1.2), visible on every tab. On
 *     mobile this collapses to a single button that opens <FilterModal />.
 *   - The granularity picker lives INSIDE the FilterBar (v1.4), not in a
 *     sub-bar of its own. On tabs with no time-series (/feedback, /jira) it
 *     is greyed out rather than removed — see lib/filter-applicability.ts.
 *     The /user/:username drill-down DOES render time-series charts
 *     (Plan 04-09) so it keeps a live picker.
 *   - <main id="dashboard-main">: anchor target for SkipToContent; receives
 *     the <Outlet/> of the active tab (OverviewPage, EngagementPage…).
 *
 * Horizontal-scroll guard (Plan 04-10, IA §9): the outer container sets
 * `overflow-x-hidden` as a last-resort clip. Individual widgets handle their
 * own overflow (tables use overflow-x-auto, charts use Recharts
 * ResponsiveContainer). The global body rule is NOT touched — we scope the
 * clip to the dashboard shell so other app routes are unaffected.
 *
 * The stale dot at the top-right indicates Langfuse sync freshness per IA
 * §1.7 — currently a static grey circle; Plan 05 replaces it with a real
 * `<StaleStatusCard>` wired to `/salescoach/api/sync-status`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { FilterBar } from '@/components/FilterBar';
import { StaleStatusDot } from '@/components/StaleStatusDot';
import { useSyncStatus } from '@/hooks/use-sync-status';
import { useTabSwipe } from '@/hooks/use-tab-swipe';
import { useViewport } from '@/hooks/use-viewport';
import { inapplicableAxes } from '@/lib/filter-applicability';
import { ageToHuman } from '@/lib/stale';

/** Tab entries shown in the top nav (order = IA §1.1; /skills appended in Phase 9 K-09). */
const TABS: Array<{ to: string; label: string }> = [
  { to: '/overview', label: 'Overview' },
  { to: '/engagement', label: 'Engagement' },
  { to: '/quality', label: 'Quality' },
  { to: '/feedback', label: 'Feedback' },
  { to: '/briefs', label: 'Briefs' },
  { to: '/inbox', label: 'Inbox' },
  { to: '/contacts', label: 'Contacts' },
  { to: '/campaigns', label: 'Campaigns' },
  { to: '/practice', label: 'Practice' },
  { to: '/operations', label: 'Operations' },
  { to: '/skills', label: 'Skills' },
  { to: '/jira', label: 'Jira' },
];

export function DashboardLayout() {
  const { pathname, search } = useLocation();
  // Which filter axes cannot describe THIS tab's population — one declaration
  // for both the desktop bar and the mobile sheet. Replaces the three
  // hand-written per-pathname rules that used to live here (granularity off on
  // /feedback, off on /jira, Type off on /inbox), which the mobile sheet never
  // saw. See lib/filter-applicability.ts.
  const inapplicable = inapplicableAxes(pathname);

  // Tab links preserve the shared filters and per-table sort keys. A campaign
  // selection belongs only to Campaigns; other tabs receive the remaining
  // query parameters, so selecting a card does not change their deep links.
  const tabSearch = new URLSearchParams(search);
  tabSearch.delete('campaign');

  // Agent N — nav scroll container ref. When the route changes on mobile
  // (horizontally scrollable tab strip), we scroll the currently-active
  // NavLink into view so the user never lands on a page whose tab is
  // off-screen. Uses inline="nearest" with smooth behavior so a re-entry
  // to the same tab is a no-op.
  const navRef = useRef<HTMLElement | null>(null);

  // Right-edge fade overlay: visible only when there is scrollable tab
  // content to the right. Users on 390px viewports were landing on
  // /overview with Feedback/Briefs/Operations offscreen and no visual
  // hint that the strip scrolled (iOS Safari hides overlay scrollbars).
  // We track `hasRightScroll` per scroll/resize and flip the fade on.
  const [hasRightScroll, setHasRightScroll] = useState(false);

  const recomputeOverflow = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    // 1px tolerance guards against sub-pixel rounding where
    // scrollLeft + clientWidth lands a hair short of scrollWidth.
    setHasRightScroll(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1);
  }, []);

  const scrollActiveIntoView = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector<HTMLElement>('a[aria-current="page"]');
    if (active) {
      active.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, []);

  useEffect(() => {
    scrollActiveIntoView();
    // DOM may not have laid out yet on first paint — re-run once the
    // browser has committed layout so the scrollWidth check is accurate.
    const raf = requestAnimationFrame(recomputeOverflow);
    // Briefs audit — `scrollIntoView({behavior: 'smooth'})` is async, so
    // the rAF above still reads the pre-animation scrollLeft. Listen for
    // `scrollend` (modern browsers) and a 400ms safety fallback so the
    // right-edge fade re-evaluates once the smooth scroll has settled.
    // The onScroll handler on the nav covers user-driven scrolls; this
    // block handles programmatic scrolls triggered by route change.
    const nav = navRef.current;
    const onScrollEnd = () => recomputeOverflow();
    nav?.addEventListener('scrollend', onScrollEnd as EventListener);
    const fallback = window.setTimeout(recomputeOverflow, 400);
    return () => {
      cancelAnimationFrame(raf);
      nav?.removeEventListener('scrollend', onScrollEnd as EventListener);
      window.clearTimeout(fallback);
    };
  }, [pathname, recomputeOverflow, scrollActiveIntoView]);

  // Re-center the active tab + re-check overflow on viewport resize
  // (rotations, DevTools docking, modal close that triggers a reflow).
  // Modal close doesn't fire a resize event itself, but the layout-shift
  // from overflow-hidden/body-scroll-lock toggles tends to, and this
  // effect is cheap enough to run on every 'resize'.
  useEffect(() => {
    const onResize = () => {
      scrollActiveIntoView();
      recomputeOverflow();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recomputeOverflow, scrollActiveIntoView]);

  // Mobile-only horizontal swipe navigation between primary tabs.
  // Touch handlers are attached to `<main>` below; the hook itself no-ops
  // when `enabled` is false (desktop / tablet) so we avoid binding listeners
  // for pointer users. See use-tab-swipe.ts for threshold & rejection rules.
  const { isMobile } = useViewport();
  const swipeHandlers = useTabSwipe(TABS, isMobile);

  // Plan 05 Task 5.8 — replace the grey-dot placeholder with a real stale
  // indicator driven by the /salescoach/api/sync-status endpoint. Polls every 60s
  // (see `useSyncStatus`).
  const sync = useSyncStatus();
  const syncLastSyncedAt = sync.data?.lastSyncedAt
    ? new Date(sync.data.lastSyncedAt)
    : null;

  return (
    <div
      className="min-h-screen w-full bg-background text-foreground"
      data-testid="dashboard-dashboard-root"
    >
      {/* Accessibility: keyboard-first skip link — invisible until focused.
          Jumps focus past the nav + filters on Tab, matching WCAG 2.4.1. */}
      <a
        href="#dashboard-main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-theme-background focus:px-3 focus:py-2 focus:text-sm focus:text-theme-text focus:shadow focus:outline focus:outline-2 focus:outline-theme-primary"
      >
        Skip to content
      </a>

      {/*
        Bug D17 — sticky header + FilterBar. The earlier fix (F3) set
        `sticky top-0 z-30` but the root `<div>` above still carried
        `overflow-x-hidden`. Per CSS spec, setting any `overflow-*` value
        other than `visible` on a single axis implicitly upgrades the
        other axis from `visible` to `auto` — so `overflow-x-hidden`
        silently turned the root into a vertical scroll container, and the
        sticky ancestor anchored to *that* container instead of the
        viewport/body. Scroll events on the real viewport never offset the
        sticky rule, so the chrome scrolled off-screen.
        Fix: drop `overflow-x-hidden` from the root (the page-level scroll
        lives on `<body>` — index.css sets `overflow-y: scroll`) and scope
        the horizontal clip to `<main>` instead, where it was only ever
        needed to guard against widgets overflowing their column. With the
        root no longer scroll-capable, `sticky top-0` anchors against the
        viewport and the header + FilterBar stay pinned. Bumped z to
        `z-50` so the sticky chrome sits above all page-level overlays
        (Recharts tooltips at z-20, popovers at z-40).
      */}
      <div
        className="sticky top-0 z-50 border-b border-theme-border bg-theme-background"
        data-testid="dashboard-sticky-chrome"
      >
        <header className="border-b border-theme-border bg-theme-background/80 backdrop-blur">
          <div className="flex items-center gap-3 px-3 h-14 sm:gap-6 sm:px-6">
            {/* Agent N — compact the product-name label at the very narrow
                end of the scale. On 375px viewports stealing gutter for the
                full wordmark is painful. We keep the short "ME" (Mr. Edge) at <sm and
                the full wordmark from sm+. */}
            <span className="shrink-0 font-semibold text-theme-text">
              <span className="sm:hidden text-base">ME</span>
              <span className="hidden sm:inline">Mr. Edge</span>
            </span>
            {/*
              Tab list: on mobile (default) the strip scrolls horizontally with
              scroll-snap so each tab snaps; `[-webkit-overflow-scrolling:touch]`
              preserves momentum scrolling on iOS. `[&::-webkit-scrollbar]:hidden`
              hides the scrollbar without collapsing scroll height.
              Agent N — a right-edge gradient fades the final tab into the
              background so users get a visual affordance that the strip is
              scrollable. Achieved with a sibling overlay so the nav itself
              stays a plain scroll container (no mask-image compatibility
              issues in older iOS Safari).
            */}
            <div className="relative flex-1 min-w-0">
              <nav
                ref={navRef}
                onScroll={recomputeOverflow}
                // Thin scrollbar is restored — users couldn't tell the strip
                // scrolled at all when it was fully hidden. `h-1` keeps the
                // track subtle; thumb colours inherit the theme's border so
                // we don't introduce a new palette entry.
                className="flex items-center gap-4 overflow-x-auto snap-x snap-mandatory [-webkit-overflow-scrolling:touch] [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-theme-border [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent pr-6"
                data-testid="dashboard-tab-nav"
              >
                {TABS.map((tab) => (
                  <NavLink
                    key={tab.to}
                    to={{ pathname: tab.to, search: tab.to === '/campaigns' ? search : tabSearch.toString() }}
                    // `end` ensures the active-state match doesn't bleed across
                    // nested routes; the object form of `to` alone would still
                    // match a prefix. /overview should only highlight on
                    // exactly /overview, not /overview/anything.
                    end
                    className={({ isActive }) =>
                      `shrink-0 snap-start px-1 text-sm transition-colors min-h-11 flex items-center ${
                        isActive
                          ? 'text-theme-text font-medium border-b-2 border-theme-primary'
                          : 'text-theme-text-secondary hover:text-theme-text'
                      }`
                    }
                  >
                    {tab.label}
                  </NavLink>
                ))}
              </nav>
              {/* Fade overlay — visible only when there's actually more
                  content scrolled off to the right (`hasRightScroll`). We
                  keep the `md:hidden` guard as a second defence so the
                  fade never appears on wide viewports where all tabs fit.
                  `pointer-events-none` so taps pass through to the links. */}
              {hasRightScroll && (
                <div
                  aria-hidden
                  className="md:hidden pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-theme-background to-transparent"
                />
              )}
            </div>
            {/* L1 — sync status consolidated into the top-right chrome. The
                card on Overview was removed; the gray text next to the dot
                carries the same "last synced · N traces total" signal without
                stealing a column in the Overview grid. */}
            <div
              className="flex items-center gap-2 shrink-0"
              data-testid="sync-status-chrome"
            >
              <StaleStatusDot
                lastSyncedAt={syncLastSyncedAt}
                ageMs={sync.data?.ageMs ?? null}
                totalTraces={sync.data?.totalTraces}
                loading={sync.isLoading}
              />
              {/*
                Operations P2-6 — on mobile the chip used to collapse to the
                bare dot, leaving no cue for what the green/red colour meant.
                The compact form (e.g. "2m") keeps the freshness signal legible
                at 390px without re-stealing the full sm+ phrasing.
                Engagement P2-02 — `sync.data` coalesced loading, error, and
                success into the same "Sync status unavailable" fallback. React
                Query exposes `isError` separately so failures can say so out
                loud instead of looking idle.
              */}
              <span
                className="text-xs text-muted-foreground tabular-nums"
                data-testid="sync-status-text"
              >
                {sync.isLoading ? (
                  <>
                    <span className="hidden sm:inline">Loading sync status…</span>
                    <span className="sm:hidden">…</span>
                  </>
                ) : sync.isError ? (
                  <>
                    <span className="hidden sm:inline text-destructive">
                      Sync status unavailable
                    </span>
                    <span className="sm:hidden text-destructive">err</span>
                  </>
                ) : sync.data ? (
                  <>
                    <span className="hidden sm:inline">
                      Last synced {ageToHuman(sync.data.ageMs)}
                    </span>
                    <span className="sm:hidden">
                      {ageToHuman(sync.data.ageMs)}
                    </span>
                  </>
                ) : (
                  <span className="hidden sm:inline">Sync status unavailable</span>
                )}
              </span>
            </div>
          </div>
        </header>

        {/* Global FilterBar (IA §1.2) — granularity is inlined as a segmented
            control inside the same strip (v1.4) instead of a second sub-bar.
            Controls the current tab cannot use are greyed out with a reason
            rather than removed, so the bar keeps one shape on every tab. */}
        <FilterBar inapplicable={inapplicable} />
      </div>

      {/* Plan 04-10: reduced horizontal padding at mobile (px-4), restored at
          sm+ (px-6). Tailwind's default `p-6` would push content off a 375px
          viewport on cards that render a fixed-width inner element.
          Bug D17: the horizontal clip that used to live on the root moves
          here — `overflow-x-hidden` on `<main>` does NOT break sticky because
          the sticky chrome is a sibling, not a descendant. Page-level
          vertical scroll continues to happen on `<body>` per index.css.
          v1.4 pass 8 — the swipe pane lives INSIDE <main>: touch handlers
          on the outer element observe the full gesture surface, but the
          `transform: translate3d(...)` is applied to an inner wrapper so the
          sticky header + FilterBar don't get dragged along with the content.
          We listen to onTransitionEnd on the inner wrapper (the element that
          actually owns the `transform` transition); a bubbling listener on
          <main> would fire for every child transition (e.g., Radix primitive
          fades) and thrash our state machine. */}
      <main
        id="dashboard-main"
        className="w-full max-w-full overflow-x-hidden px-4 py-4 sm:p-6"
        onTouchStart={swipeHandlers.onTouchStart}
        onTouchMove={swipeHandlers.onTouchMove}
        onTouchEnd={swipeHandlers.onTouchEnd}
        data-swipe-enabled={isMobile ? 'true' : 'false'}
      >
        <div
          data-testid="dashboard-swipe-pane"
          onTransitionEnd={(e) => {
            // Only react to our own transform transition. Child elements
            // (Radix fades, Tailwind color transitions, chart tweens) bubble
            // transitionend up to us; filtering on propertyName keeps the
            // state machine deterministic.
            if (e.propertyName !== 'transform') return;
            if (e.currentTarget !== e.target) return;
            swipeHandlers.onTransitionEnd(e);
          }}
          style={
            isMobile
              ? {
                  transform: `translate3d(${swipeHandlers.dragOffset}px, 0, 0)`,
                  transition: swipeHandlers.animating
                    ? `transform ${swipeHandlers.transitionMs}ms cubic-bezier(0.2, 0.9, 0.3, 1)`
                    : 'none',
                  willChange: 'transform',
                  touchAction: 'pan-y', // allow vertical scroll, intercept horizontal
                }
              : undefined
          }
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}
